import _ from "lodash";
import { LngLat } from "mapbox-gl";
import { useEffect, useMemo, useState } from "react";
import { useLeadStore, useMapStore, usePipedriveStore } from "..";
import ISearchResult from "../interfaces/ISearchResult";
import SearchType from "../interfaces/SearchType";
import MapboxApiService from "../services/mapboxApiService";
import useDealsWithFocusArea from "./useDealsWithFocusArea";
import useDealsWithGlobalFilters from "./useDealsWithGlobalFilters";
import useLeadsWithFocusArea from "./useLeadsWithFocusArea";
import useLeadsWithGlobalFilters from "./useLeadsWithGlobalFilters";

export default function useSearch(query: string, type: SearchType): ISearchResult {
    const mapboxApiService = useMemo(() => new MapboxApiService(), []);
    const pipedriveStore = usePipedriveStore();
    const mapStore = useMapStore();
    const leadStore = useLeadStore();
    const deals = useDealsWithFocusArea(useDealsWithGlobalFilters(pipedriveStore.deals));
    const leads = useLeadsWithFocusArea(useLeadsWithGlobalFilters(leadStore.leads));
    const [searchResult, setSearchResult] = useState<ISearchResult>({});

    useEffect(() => {
        setSearchResult({});
        if (query.length < 3) {
            return;
        }

        let isRequestCancelled = false;
        if (type === SearchType.Address) {
            mapboxApiService.addressLocationSearch(query, new LngLat(mapStore.viewState.longitude, mapStore.viewState.latitude))
                .then(locations => !isRequestCancelled && setSearchResult({
                    foundLocations: locations,
                }));
        } else if (type === SearchType.Deals) {
            const queryInLowerCase = query.toLowerCase();
            const result = deals.filter(d =>
                d.address && d.address.toLowerCase().includes(queryInLowerCase) ||
                d.name && d.name.toLowerCase().includes(queryInLowerCase));

            setSearchResult({
                foundDeals: _.orderBy(result, deal => deal.updatedAt, "desc").slice(0, 10),
            });
        } else if (type === SearchType.Leads) {
            const queryInLowerCase = query.toLowerCase();
            const result = leads.filter(l =>
                (l.address && l.address.toLowerCase().includes(queryInLowerCase)) ||
                (l.ownerName && l.ownerName.toLowerCase().includes(queryInLowerCase)) ||
                (l.ownerAddress && l.ownerAddress.toLowerCase().includes(queryInLowerCase)) ||
                (l.phoneNumber && l.phoneNumber.toLowerCase().includes(queryInLowerCase)) ||
                (l.pipedriveCompanyId && pipedriveStore.organizations.find(o => o.id === l.pipedriveCompanyId)?.name.toLowerCase().includes(queryInLowerCase)) ||
                (l.notes.some(n => n.text.toLowerCase().includes(queryInLowerCase))));

            setSearchResult({
                foundLeads: _.orderBy(result, lead => lead.modifiedDate, "desc").slice(0, 10),
            });
        }

        return () => {
            isRequestCancelled = true;
        };
    }, [query, type, deals, leads]);

    return searchResult;
}